import React, { useState, useEffect } from "react";
import { Icon } from "semantic-ui-react";
import { useDispatch } from "react-redux";
import ReportDocumentModal from "../ReportDocumentModal/ReportDocumentModal";
import { ReportDocumentType } from "src/graphql/documentReports";
import { setUpdateDocuments } from "src/redux/actions/documentostributarios";
import { importUpdateTransactions } from "src/redux/actions/conciliacion";
import { dt } from "src/utils/documentsI18n";
import "./ReportDocumentButton.scss";

/**
 * Resolves the ReportDocumentType from document metadata.
 */
const resolveDocumentType = (data) => {
  if (!data) return ReportDocumentType.TAX_DOCUMENT;
  const docType = data.documentType;
  if (docType === "OBLIGATION") return ReportDocumentType.OBLIGATION;
  if (docType === "CREDIT") return ReportDocumentType.CREDIT;
  const code = data.meta?.documentCode;
  if ([0, 1, "0", "1"].includes(code)) return ReportDocumentType.FEE;
  return ReportDocumentType.TAX_DOCUMENT;
};

/**
 * ReportDocumentButton — compact, right-aligned button to report a document/transaction problem.
 *
 * @param {object}  data             - The document or transaction object (needs id/_id, isReported, meta, entries, etc.)
 * @param {string}  [documentType]   - Explicit ReportDocumentType. If omitted, resolved automatically from data.
 * @param {boolean} [isTransaction]  - True when reporting a bank transaction (dispatches importUpdateTransactions instead).
 * @param {function} [onSuccess]     - Optional extra callback after a successful report.
 */
const ReportDocumentButton = ({ data, documentType, isTransaction = false, onSuccess }) => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [isReported, setIsReported] = useState(data?.isReported || false);

  useEffect(() => {
    setIsReported(data?.isReported || false);
  }, [data?.id, data?._id, data?.isReported]);

  const resolvedType = documentType || resolveDocumentType(data);

  const documentId = data?.id || data?._id;

  const hasMatches = isTransaction
    ? (Array.isArray(data?.matchs) ? data.matchs : []).some(
        (match) => match?.docId != null && String(match.docId).trim() !== ""
      )
    : (Array.isArray(data?.meta?.matchs?.inMatch)
        ? data.meta.matchs.inMatch
        : []
      ).some((match) => match?.id != null && String(match.id).trim() !== "") ||
      (Array.isArray(data?.meta?.matchs?.outMatch)
        ? data.meta.matchs.outMatch
        : []
      ).some((match) => match?.id != null && String(match.id).trim() !== "");

  const entriesData = data?.entries || data?.entryStatus;
  const hasAsientos = !!(
    entriesData?.completed === true ||
    Number(entriesData?.amount || 0) > 0 ||
    (Array.isArray(entriesData?.entryList) && entriesData.entryList.length > 0)
  );

  const handleSuccess = () => {
    setIsReported(true);
    setTimeout(() => {
      if (isTransaction) {
        dispatch(importUpdateTransactions(true));
      } else {
        dispatch(setUpdateDocuments({ value: true }));
      }
    }, 500);
    onSuccess?.();
  };

  if (!documentId) return null;

  return (
    <>
      <button
        className={`report-btn-compact ${isReported ? "reported" : ""}`}
        onClick={() => !isReported && setShowModal(true)}
        disabled={isReported}
        title={isReported ? dt("report.documentReported") : dt("report.reportProblem")}
      >
        <Icon name="flag" size="small" />
        <span>{isReported ? dt("report.reported") : dt("report.report")}</span>
      </button>

      <ReportDocumentModal
        open={showModal}
        onClose={() => setShowModal(false)}
        documentId={documentId}
        documentType={resolvedType}
        onReportSuccess={handleSuccess}
        hasMatches={hasMatches}
        hasAsientos={hasAsientos}
      />
    </>
  );
};

export default ReportDocumentButton;
